import React, { useMemo } from "react";
import {
  Shield,
  Activity,
  LayoutGrid,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Zap,
  BarChart3,
} from "lucide-react";

interface StandardSummary {
  id: number;
  standard_name: string;
  total_checks: number;
  passed: number;
  failed: number;
  score_pct: number;
  last_scanned_at: string;
}

interface GroupSummary {
  id: number;
  group_id: string;
  group_label: string;
  total_checks: number;
  passed: number;
  failed: number;
  score_pct: number;
  last_scanned_at: string;
}

interface ComplianceOverviewProps {
  standards: StandardSummary[];
  groups: GroupSummary[];
  onSelectStandard: (std: StandardSummary) => void;
}

const ComplianceOverview: React.FC<ComplianceOverviewProps> = ({
  standards,
  groups,
  onSelectStandard,
}) => {
  // Aggregate stats from API data
  const aggregated = useMemo(() => {
    let totalChecks = 0;
    let totalPassed = 0;
    let totalFailed = 0;
    let scoreSum = 0;

    standards.forEach((std) => {
      totalChecks += std.total_checks;
      totalPassed += std.passed;
      totalFailed += std.failed;
      scoreSum += std.score_pct;
    });

    const avgScore = standards.length > 0 ? scoreSum / standards.length : 0;

    return { totalChecks, totalPassed, totalFailed, avgScore };
  }, [standards]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return { text: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20", hex: "#10b981", hexEnd: "#6ee7b7" };
    if (score >= 50) return { text: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20", hex: "#f59e0b", hexEnd: "#fde68a" };
    return { text: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20", hex: "#ef4444", hexEnd: "#fca5a5" };
  };

  const getStatusLabel = (score: number) => {
    if (score >= 80) return "Compliant";
    if (score >= 50) return "Review";
    return "At Risk";
  };

  // Semi-arc for hero widget
  const arcRadius = 44;
  const arcCircumference = Math.PI * arcRadius; // half-circle
  const arcOffset = arcCircumference - (aggregated.avgScore / 100) * arcCircumference;
  const heroColor = getScoreColor(aggregated.avgScore);

  // Find max total_checks among groups for relative bar sizing
  // const maxGroupChecks = useMemo(() => Math.max(...groups.map(g => g.total_checks), 1), [groups]);

  return (
    <div className="space-y-4 animate-in fade-in duration-300">

      {/* ═══ TOP METRICS — Compact horizontal strip ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">

        {/* Metric 1: Semi-arc compliance score */}
        <div className="relative overflow-hidden bg-gray-800 border border-gray-700/50 rounded-xl p-3 flex flex-col items-center">
          <span className="text-[9px] font-bold text-gray-200 uppercase tracking-[0.12em] self-start flex items-center gap-1 mb-1">
            <Zap className="w-2.5 h-2.5 text-orange-500" />
            Compliance Index
          </span>
          {/* Semi-circle arc meter */}
          <svg width="100" height="56" viewBox="0 0 100 56" className="mt-0.5">
            <defs>
              <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={heroColor.hex} />
                <stop offset="100%" stopColor={heroColor.hexEnd} />
              </linearGradient>
            </defs>
            {/* Track */}
            <path
              d="M 6 52 A 44 44 0 0 1 94 52"
              fill="none" stroke="#7e8896ff" strokeWidth="6" strokeLinecap="round"
            />
            {/* Score arc */}
            <path
              d="M 6 52 A 44 44 0 0 1 94 52"
              fill="none" stroke="url(#arcGrad)" strokeWidth="6" strokeLinecap="round"
              strokeDasharray={arcCircumference}
              strokeDashoffset={arcOffset}
              style={{ transition: "stroke-dashoffset 1s ease" }}
            />
            {/* Center text */}
            <text x="50" y="48" textAnchor="middle" className="fill-white text-lg font-black"
              style={{ fontSize: "20px", fontWeight: 900 }}>
              {aggregated.avgScore.toFixed(0)}%
            </text>
          </svg>
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${heroColor.bg} ${heroColor.text} ${heroColor.border} border mt-1`}>
            {getStatusLabel(aggregated.avgScore)}
          </span>
        </div>

        {/* Metric 2: Total checks */}
        <div className="bg-gray-800 border border-gray-700/50 rounded-xl p-3 flex flex-col justify-between">
          <span className="text-[9px] font-bold text-gray-200 uppercase tracking-[0.12em] flex items-center gap-1">
            <Shield className="w-2.5 h-2.5 text-gray-200" />
            Total Checks
          </span>
          <span className="text-2xl font-black text-white mt-1">{aggregated.totalChecks}</span>
          <span className="text-[9px] text-gray-200 mt-0.5">audit requirements</span>
        </div>

        {/* Metric 3: Passed */}
        <div className="bg-gray-800 border border-gray-700/50 rounded-xl p-3 flex flex-col justify-between">
          <span className="text-[9px] font-bold text-gray-200 uppercase tracking-[0.12em] flex items-center gap-1">
            <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" />
            Passed
          </span>
          <span className="text-2xl font-black text-emerald-400 mt-1">{aggregated.totalPassed}</span>
          <div className="w-full h-1 rounded-full bg-gray-900 mt-1.5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-700"
              style={{ width: `${aggregated.totalChecks > 0 ? (aggregated.totalPassed / aggregated.totalChecks) * 100 : 0}%` }} />
          </div>
        </div>

        {/* Metric 4: Failed */}
        <div className="bg-gray-800 border border-gray-700/50 rounded-xl p-3 flex flex-col justify-between">
          <span className="text-[9px] font-bold text-gray-200 uppercase tracking-[0.12em] flex items-center gap-1">
            <XCircle className="w-2.5 h-2.5 text-rose-500" />
            Failed
          </span>
          <span className="text-2xl font-black text-rose-400 mt-1">{aggregated.totalFailed}</span>
          <div className="w-full h-1 rounded-full bg-gray-900 mt-1.5 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-rose-400 transition-all duration-700"
              style={{ width: `${aggregated.totalChecks > 0 ? (aggregated.totalFailed / aggregated.totalChecks) * 100 : 0}%` }} />
          </div>
        </div>
      </div>

      {/* ═══ STANDARDS — Horizontal bar chart cards ═══ */}
      <div className="space-y-2.5">
        <div className="flex items-center gap-2">
          <div className="p-1 bg-orange-500 rounded">
            <BarChart3 className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-xs font-extrabold text-white tracking-wide">Scanned Standards</h2>
          <div className="flex-grow border-t border-gray-700/40 ml-2" />
          <span className="text-[9px] text-gray-200 font-bold bg-gray-800 border border-gray-700/50 px-2 py-0.5 rounded-full">
            {standards.length}
          </span>
        </div>

        <div className="space-y-2">
          {standards.map((std) => {
            const sc = getScoreColor(std.score_pct);
            const passRatio = std.total_checks > 0 ? (std.passed / std.total_checks) * 100 : 0;

            return (
              <div
                key={std.id}
                onClick={() => onSelectStandard(std)}
                className="group bg-gray-800 border border-gray-700/50 rounded-xl p-3 cursor-pointer hover:border-orange-500/40 transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  {/* Left: Name + score badge */}
                  <div className="flex items-center gap-2 min-w-0 flex-shrink-0" style={{ width: "180px" }}>
                    <div className={`w-7 h-7 rounded-lg ${sc.bg} ${sc.border} border flex items-center justify-center flex-shrink-0`}>
                      <Shield className="w-3.5 h-3.5" style={{ color: sc.hex }} />
                    </div>
                    <div className="min-w-0">
                      <span className="font-extrabold text-[11px] text-white uppercase tracking-wide block truncate">
                        {std.standard_name}
                      </span>
                      <span className="text-[8px] text-gray-200">{std.total_checks} checks</span>
                    </div>
                  </div>

                  {/* Center: Stacked horizontal bar chart */}
                  <div className="flex-grow mx-2">
                    <div className="w-full h-5 rounded-lg overflow-hidden bg-gray-900/80 flex border border-gray-700/30 relative">
                      {std.passed > 0 && (
                        <div
                          className="h-full flex items-center justify-end pr-1.5 transition-all duration-700 relative z-10"
                          style={{
                            width: `${passRatio}%`,
                            background: `linear-gradient(90deg, #10b98140, #10b98190)`,
                          }}
                        >
                          {passRatio > 15 && (
                            <span className="text-[8px] font-black text-white/90">{std.passed}</span>
                          )}
                        </div>
                      )}
                      {std.failed > 0 && (
                        <div
                          className="h-full flex items-center justify-start pl-1.5 transition-all duration-700"
                          style={{
                            width: `${100 - passRatio}%`,
                            background: `linear-gradient(90deg, #ef444460, #ef444490)`,
                          }}
                        >
                          {(100 - passRatio) > 15 && (
                            <span className="text-[8px] font-black text-white/90">{std.failed}</span>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Micro legend below bar */}
                    <div className="flex justify-between mt-0.5 text-[8px] text-gray-200">
                      <span>✓ {std.passed} passed</span>
                      <span>✕ {std.failed} failed</span>
                    </div>
                  </div>

                  {/* Right: Score + action */}
                  <div className="flex items-center gap-2.5 flex-shrink-0">
                    <span className={`text-base font-black ${sc.text} tabular-nums min-w-[40px] text-right`}>
                      {std.score_pct.toFixed(0)}%
                    </span>
                    <span className="text-orange-400/70 group-hover:text-orange-400 transition-colors">
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══ SECURITY GROUPS — Heat-map tile grid ═══ */}
      <div className="space-y-2.5">
        <div className="flex items-center gap-2">
          <div className="p-1 bg-orange-500 rounded-md">
            <LayoutGrid className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-xs font-extrabold text-white tracking-wide">Security Groups</h2>
          <div className="flex-grow border-t border-gray-700/40 ml-2" />
          <span className="text-[9px] text-gray-200 font-bold bg-gray-800 border border-gray-700/50 px-2 py-0.5 rounded-full">
            {groups.length}
          </span>
        </div>

        {groups.length > 0 ? (
          <div className="bg-gray-800 border border-gray-700/50 rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[9px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700/40 bg-gray-900/40">
              <div className="col-span-4">Group</div>
              <div className="col-span-3 text-center">Coverage</div>
              <div className="col-span-2 text-center">Pass / Fail</div>
              <div className="col-span-1 text-center">Score</div>
              <div className="col-span-2 text-center">Status</div>
            </div>
            {/* Rows */}
            {groups.map((group, idx) => {
              const gc = getScoreColor(group.score_pct);
              // const gPassRatio = group.total_checks > 0 ? (group.passed / group.total_checks) * 100 : 0;
              // const barWidth = (group.total_checks / maxGroupChecks) * 100;

              return (
                <div
                  key={group.id}
                  className={`grid grid-cols-12 gap-2 px-3 py-2 items-center text-[10px] transition-colors hover:bg-gray-700/20 ${idx < groups.length - 1 ? "border-b border-gray-700/20" : ""}`}
                >
                  {/* Group label */}
                  <div className="col-span-4 flex items-center gap-1.5 min-w-0">
                    {/* Heat dot */}
                    <span className="w-2 h-2 rounded-full flex-shrink-0 shadow-sm"
                      style={{
                        background: gc.hex,
                        boxShadow: `0 0 4px ${gc.hex}60`,
                      }}
                    />
                    <span className="font-bold text-white truncate">{group.group_label}</span>
                  </div>

                  {/* Coverage: stacked pass/fail bar */}
                  <div className="col-span-3 flex items-center gap-1.5">
                    <div className="flex-grow h-2 rounded-full bg-gray-900/80 overflow-hidden border border-gray-700/30 flex">
                      {group.passed > 0 && (
                        <div
                          className="h-full transition-all duration-500"
                          style={{
                            width: `${group.total_checks > 0 ? (group.passed / group.total_checks) * 100 : 0}%`,
                            background: `linear-gradient(90deg, #10b98150, #10b981)`,
                          }}
                        />
                      )}
                      {group.failed > 0 && (
                        <div
                          className="h-full transition-all duration-500"
                          style={{
                            width: `${group.total_checks > 0 ? (group.failed / group.total_checks) * 100 : 0}%`,
                            background: `linear-gradient(90deg, #ef444460, #ef4444)`,
                          }}
                        />
                      )}
                    </div>
                    <span className="text-[9px] text-gray-200 font-mono flex-shrink-0 w-6 text-right">{group.total_checks}</span>
                  </div>

                  {/* Pass / Fail */}
                  <div className="col-span-2 text-center font-bold">
                    <span className="text-emerald-400">{group.passed}</span>
                    <span className="text-gray-200 mx-0.5">/</span>
                    <span className="text-rose-400">{group.failed}</span>
                  </div>

                  {/* Score with mini inline bar */}
                  <div className="col-span-1 text-center">
                    <span className={`font-black ${gc.text}`}>{group.score_pct.toFixed(0)}%</span>
                  </div>

                  {/* Status chip */}
                  <div className="col-span-2 flex justify-center">
                    <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-md ${gc.bg} ${gc.text} ${gc.border} border`}>
                      {group.score_pct >= 80 ? <CheckCircle2 className="w-2.5 h-2.5" /> : group.score_pct >= 50 ? <Activity className="w-2.5 h-2.5" /> : <XCircle className="w-2.5 h-2.5" />}
                      {getStatusLabel(group.score_pct)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-200 text-xs bg-gray-800/30 border border-gray-700/40 rounded-xl flex flex-col items-center gap-1.5">
            <LayoutGrid className="w-5 h-5 text-gray-200" />
            <span className="font-semibold">No security group metrics scanned</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceOverview;
